import { ColumnDef } from '@tanstack/react-table';
import { t } from 'i18next';
import { EllipsisVerticalIcon, RefreshCcwIcon, SquarePenIcon, Trash2Icon } from 'lucide-react';

import { Avatar } from '@/components/atoms/Avatar/Avatar/Avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SortableHeader } from '@/components/ui/table/sortable-header';
import { Member, Invitation, RoleType } from '@/shared/types/member';
import { Role } from '@/shared/types/role';
import { Team } from '@/shared/types/team';
import { COLUMN_PRESETS } from '@/util/constants';
import { getRoleName } from '@/util/memberUtils';

export interface MemberWithActions extends Member {
  onEditRole?: () => void;
  onTransferAccount?: () => void;
  onDeleteMember?: () => void;
  canAssign?: boolean;
  isCurrentUser?: boolean;
  isOwner?: boolean;
}

export interface InvitationWithActions extends Invitation {
  name: string;
  role_ids: string[];
  onEditRole?: () => void;
  onTransferAccount?: () => void;
  onDeleteMember?: () => void;
  canAssign?: boolean;
  isCurrentUser?: boolean;
  isOwner?: boolean;
}

export type MemberOrInvitation = MemberWithActions | InvitationWithActions;

interface ColumnsProps {
  roles: Role[];
  teams: Team[];
  user: { user_id: string };
  accountRole: RoleType;
  canAssign: boolean;
  onEditRole: (user: MemberOrInvitation) => void;
  onTransferAccount: (user: MemberOrInvitation) => void;
  onDeleteMember: (user: MemberOrInvitation) => void;
}

export const createColumns = ({
  roles,
  teams,
  user,
  accountRole,
  canAssign,
  onEditRole,
  onTransferAccount,
  onDeleteMember,
}: ColumnsProps): ColumnDef<MemberOrInvitation>[] => {
  return [
    {
      accessorKey: 'name',
      ...COLUMN_PRESETS.FLEX,
      meta: { preset: 'FLEX', className: 'pl-6' },
      header: ({ column }) => <SortableHeader column={column}>{t('common.name')}</SortableHeader>,
      cell: ({ row }) => {
        const memberOrInvitation = row.original;
        const value = memberOrInvitation.name || memberOrInvitation.email;

        if ('invitation_code' in memberOrInvitation) {
          const invitationAvatar = memberOrInvitation.user_avatar;
          return (
            <div className="flex items-center gap-2 text-foreground-muted">
              <Avatar src={invitationAvatar} />
              <span className="truncate text-xs leading-4">{value}</span>
              <Badge type="outline" flavour="neutral" rounded={false}>
                {t('status.pending')}
              </Badge>
            </div>
          );
        }

        const { user_id, email } = memberOrInvitation;
        return (
          <div className="flex items-center gap-2">
            <Avatar userId={user_id} />
            <div className="flex min-w-0 flex-col">
              <span className="truncate">{value}</span>
              <span className="truncate text-xs leading-4 text-foreground-muted">{email}</span>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: 'role_ids',
      header: t('roles.individual'),
      enableSorting: false,
      ...COLUMN_PRESETS.MD,
      meta: { preset: 'MD' },
      cell: ({ row }) => {
        return <div className="truncate">{getRoleName(row.original as Member, roles)}</div>;
      },
    },
    {
      accessorKey: 'team_ids',
      header: t('common.teams'),
      enableSorting: false,
      ...COLUMN_PRESETS.MD,
      meta: { preset: 'MD' },
      cell: ({ row }) => {
        const teamIds = row.original.team_ids;
        const teamNames = teamIds?.map((team_id: string) => {
          const name = teams.find((team) => team.team_id === team_id)?.name;
          return name;
        });
        return <div className="truncate">{teamNames?.join(', ') || '-'}</div>;
      },
    },
    {
      id: 'actions',
      header: '',
      enableSorting: false,
      ...COLUMN_PRESETS.SM,
      meta: { preset: 'SM' },
      cell: ({ row }) => {
        const memberOrInvitation = row.original;
        const isOwner = memberOrInvitation.role === RoleType.OWNER;
        const isCurrentUser = memberOrInvitation.user_id === user?.user_id;
        const isDisabled = isOwner || isCurrentUser || !canAssign;

        const menuText =
          'invitation_code' in memberOrInvitation
            ? `${t('invitations.revoke')}…`
            : memberOrInvitation.user_id
              ? `${t('members.delete')}…`
              : '';

        return (
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost-neutral" size="icon-sm" disabled={isDisabled}>
                <EllipsisVerticalIcon size={16} />
              </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => onEditRole(memberOrInvitation)}
                className="flex items-center gap-2"
              >
                <SquarePenIcon size={16} className="text-foreground-muted" />
                {`${t('roles.edit_access')}…`}
              </DropdownMenuItem>

              {accountRole === RoleType.OWNER && (
                <DropdownMenuItem
                  onClick={() => onTransferAccount(memberOrInvitation)}
                  className="flex items-center gap-2"
                >
                  <RefreshCcwIcon size={16} className="text-foreground-muted" />
                  {`${t('roles.transfer_ownership')}…`}
                </DropdownMenuItem>
              )}

              <DropdownMenuItem
                onClick={() => onDeleteMember(memberOrInvitation)}
                variant="destructive"
                className="flex items-center gap-2"
              >
                <Trash2Icon size={16} />
                {menuText}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];
};
